import {Input} from 'antd'

import styles from './SecurityItemForm.module.scss'
import {usePassRequestMilestone, isMilestonePassed} from 'modules/request'
import {
  Button,
  Form,
  Text,
  InputAmount,
  CentsMoney,
  Select,
  FormItem,
  useForm,
  useWatch,
} from 'common/components'
import {FIELD_RULES} from 'common/constants'
import {useCreateSecurityItem, useUpdateSecurityItem, SecurityItem} from '..'

interface SecurityItemFormProps {
  projectUuid: string
  request?: any
  item?: SecurityItem
  onClose: () => void
}

export default function SecurityItemForm({
  projectUuid,
  request,
  item,
  onClose,
}: SecurityItemFormProps) {
  const isEdit = Boolean(item)
  const {
    mutate: passRequestMilestone,
    isMutating: isPassRequestMilestoneMutating,
  } = usePassRequestMilestone(request?.uuid, 'security-items-added', onClose)
  const {mutate: createSecurityItem, isMutating: isCreateSecurityItemMutating} =
    useCreateSecurityItem(
      projectUuid,
      request
        ? isMilestonePassed('security-items-added', request)
          ? onClose
          : passRequestMilestone
        : onClose
    )
  const {mutate: updateSecurityItem, isMutating: isUpdateSecurityItemMutating} =
    useUpdateSecurityItem(projectUuid, item?.uuid, onClose)
  const [form] = useForm()
  const landValue = useWatch('landValue', form)
  const improvementValue = useWatch('improvementValue', form)

  const handleFinish = (values: any) => {
    if (isEdit) return updateSecurityItem(values)
    createSecurityItem(values)
  }

  const initialValues = isEdit
    ? {
        type: item.type,
        address: item.address,
        landValue: item.landValue,
        improvementValue: item.improvementValue,
      }
    : {
        type: 'land',
      }

  if (process.env.NODE_ENV === 'development') {
    initialValues.address         = 'some where';
    initialValues.landValue       = 778800;
    initialValues.improvementValue= 120000;
  }

  const securityValue = (landValue ?? 0) + (improvementValue ?? 0)

  return (
    <Form form={form} initialValues={initialValues} onFinish={handleFinish}>
      <FormItem name="type" label="Type" rules={FIELD_RULES}>
        <Select options={typeOptions} />
      </FormItem>
      <FormItem name="address" label="Address" rules={FIELD_RULES}>
        <Input />
      </FormItem>
      <FormItem name="landValue" label="Land value" rules={amountFieldRules}>
        <InputAmount size="middle" min={0} />
      </FormItem>
      <FormItem
        name="improvementValue"
        label="Improvement value"
        rules={amountFieldRules}
      >
        <InputAmount size="middle" min={0} />
      </FormItem>
      <div className={styles.footer}>
        <div>
          <Text weight="demiBold" mb={2}>
            Security value
          </Text>
          <CentsMoney weight="medium" size="3xl" value={securityValue} />
        </div>
        <Button
          htmlType="submit"
          loading={
            isCreateSecurityItemMutating ||
            isPassRequestMilestoneMutating ||
            isUpdateSecurityItemMutating
          }
        >
          Save item
        </Button>
      </div>
    </Form>
  )
}

const amountFieldRules = [
  ...FIELD_RULES,
  () => ({
    validator(_: any, value: any) {
      if (value) {
        return Promise.resolve()
      }
      return Promise.reject()
    },
  }),
]

const typeOptions = [{value: 'land', label: 'Land'}]
